import { useState, useEffect, useRef } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { 
  Plus, Trash2, Save, X, Loader2, TrendingUp, Upload, Video, Edit, Check,
  Dumbbell, Timer, Activity, ChevronDown, ChevronUp, Play, User
} from "lucide-react";
import { getImageUrl, DEFAULT_PLAYER_IMAGE } from "@/lib/imageUtils";

const PlayerProgressManager = () => {
  const { authAxios } = useAuth();
  const [players, setPlayers] = useState([]);
  const [progressData, setProgressData] = useState({});
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProgress, setEditingProgress] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [expandedPlayer, setExpandedPlayer] = useState(null);
  const fileInputRef = useRef(null);
  const editFileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    player_id: "",
    date: new Date().toISOString().split('T')[0],
    bench_press_max: "",
    squat_max: "",
    forty_yard_dash: "",
    shuttle_run: "",
    video_url: "",
    notes: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [playersRes, progressRes] = await Promise.all([
        axios.get(`${API}/players`),
        authAxios.get('/player-progress')
      ]);
      
      setPlayers(playersRes.data);
      
      // Group progress by player_id
      const grouped = {};
      progressRes.data.forEach(entry => {
        if (!grouped[entry.player_id]) {
          grouped[entry.player_id] = [];
        }
        grouped[entry.player_id].push(entry);
      });
      setProgressData(grouped);
    } catch (e) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleVideoUpload = async (e, isEdit = false) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (max 100MB)
    if (file.size > 100 * 1024 * 1024) {
      toast.error("Video file must be under 100MB");
      return;
    }

    setUploading(true);
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);
      uploadData.append('category', 'progress');

      const response = await authAxios.post('/upload', uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const videoUrl = response.data.url;
      
      if (isEdit && editingProgress) {
        setEditingProgress({ ...editingProgress, video_url: videoUrl });
      } else {
        setFormData({ ...formData, video_url: videoUrl });
      }
      toast.success("Video uploaded!");
    } catch (e) {
      toast.error("Failed to upload video");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (editFileInputRef.current) editFileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.player_id) {
      toast.error("Please select a player");
      return;
    }

    try {
      const payload = {
        ...formData,
        bench_press_max: formData.bench_press_max ? parseFloat(formData.bench_press_max) : null,
        squat_max: formData.squat_max ? parseFloat(formData.squat_max) : null,
        forty_yard_dash: formData.forty_yard_dash ? parseFloat(formData.forty_yard_dash) : null,
        shuttle_run: formData.shuttle_run ? parseFloat(formData.shuttle_run) : null,
      };
      
      await authAxios.post('/player-progress', payload);
      toast.success("Progress entry added!");
      setShowForm(false);
      resetForm();
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add progress");
    }
  };

  const handleEdit = (entry) => {
    setEditingProgress({
      ...entry,
      bench_press_max: entry.bench_press_max || "",
      squat_max: entry.squat_max || "",
      forty_yard_dash: entry.forty_yard_dash || "",
      shuttle_run: entry.shuttle_run || "",
      notes: entry.notes || ""
    });
    setShowForm(false);
  };

  const handleSaveEdit = async () => {
    if (!editingProgress) return;
    
    try {
      const payload = {
        date: editingProgress.date,
        bench_press_max: editingProgress.bench_press_max ? parseFloat(editingProgress.bench_press_max) : null,
        squat_max: editingProgress.squat_max ? parseFloat(editingProgress.squat_max) : null,
        forty_yard_dash: editingProgress.forty_yard_dash ? parseFloat(editingProgress.forty_yard_dash) : null,
        shuttle_run: editingProgress.shuttle_run ? parseFloat(editingProgress.shuttle_run) : null,
        video_url: editingProgress.video_url || null,
        notes: editingProgress.notes || null
      };
      
      await authAxios.put(`/player-progress/${editingProgress.id}`, payload);
      toast.success("Progress updated!");
      setEditingProgress(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update progress");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this progress entry?")) return;
    
    try {
      await authAxios.delete(`/player-progress/${id}`);
      toast.success("Progress entry deleted");
      if (editingProgress?.id === id) setEditingProgress(null);
      fetchData();
    } catch (e) {
      toast.error("Failed to delete progress entry");
    }
  };

  const resetForm = () => {
    setFormData({
      player_id: "",
      date: new Date().toISOString().split('T')[0],
      bench_press_max: "",
      squat_max: "",
      forty_yard_dash: "",
      shuttle_run: "",
      video_url: "",
      notes: ""
    });
  };

  const getPlayerName = (playerId) => {
    const player = players.find(p => p.id === playerId);
    return player ? player.name : "Unknown Player";
  };

  const getPlayerImage = (playerId) => {
    const player = players.find(p => p.id === playerId);
    return player?.image_url;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', { 
      year: 'numeric', month: 'short', day: 'numeric' 
    });
  };

  const StatBadge = ({ label, value, unit, icon: Icon, color }) => (
    <div className={`bg-${color}/10 border border-${color}/30 px-3 py-2 rounded`}>
      <div className="flex items-center gap-2">
        <Icon className={`w-4 h-4 text-${color}`} />
        <span className="font-orbitron text-[10px] text-gray-400 uppercase">{label}</span>
      </div>
      <p className={`font-orbitron text-lg text-${color} mt-1`}>
        {value || '--'} <span className="text-xs text-gray-500">{unit}</span>
      </p>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-neon-blue animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="font-orbitron text-xl text-white flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-neon-green" />
          Player Progress Tracking
        </h2>
        <button
          onClick={() => { setShowForm(!showForm); setEditingProgress(null); }}
          data-testid="add-progress-btn"
          className="cyber-btn py-2 px-4 text-xs"
        >
          <span className="flex items-center gap-2">
            {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {showForm ? "CANCEL" : "ADD PROGRESS"}
          </span>
        </button>
      </div>

      {/* Add Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="cyber-card p-6 space-y-4" data-testid="progress-add-form">
          <div className="flex items-center gap-2 mb-4 pb-2 border-b border-neon-green/30">
            <Plus className="w-5 h-5 text-neon-green" />
            <span className="font-orbitron text-sm text-neon-green">ADD PROGRESS ENTRY</span>
          </div>
          
          {/* Player Selection */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="font-orbitron text-xs text-gray-400">Select Player *</Label>
              <Select value={formData.player_id} onValueChange={(v) => setFormData({...formData, player_id: v})}>
                <SelectTrigger className="bg-void-black border-white/10" data-testid="progress-player-select">
                  <SelectValue placeholder="Choose a player..." />
                </SelectTrigger>
                <SelectContent className="bg-void-black border-white/10 max-h-60">
                  {players.map(player => (
                    <SelectItem key={player.id} value={player.id}>
                      <span className="flex items-center gap-2">
                        {player.name} - #{player.jersey_number || '00'} ({player.position})
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="font-orbitron text-xs text-gray-400">Date *</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({...formData, date: e.target.value})}
                required
                className="bg-void-black border-white/10"
                data-testid="progress-date-input"
              />
            </div>
          </div>

          {/* Stats */}
          <div className="border border-dashed border-neon-blue/50 p-4 space-y-4">
            <Label className="font-orbitron text-xs text-neon-blue flex items-center gap-2">
              <Dumbbell className="w-4 h-4" />
              PERFORMANCE STATS
            </Label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label className="font-orbitron text-[10px] text-gray-500 flex items-center gap-1">
                  <Dumbbell className="w-3 h-3" /> BENCH PRESS MAX
                </Label>
                <div className="relative">
                  <Input
                    type="number"
                    step="0.5"
                    value={formData.bench_press_max}
                    onChange={(e) => setFormData({...formData, bench_press_max: e.target.value})}
                    placeholder="0"
                    className="bg-void-black border-white/10 pr-12"
                    data-testid="progress-bench-input"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">lbs</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="font-orbitron text-[10px] text-gray-500 flex items-center gap-1">
                  <Dumbbell className="w-3 h-3" /> SQUAT MAX
                </Label>
                <div className="relative">
                  <Input
                    type="number"
                    step="0.5"
                    value={formData.squat_max}
                    onChange={(e) => setFormData({...formData, squat_max: e.target.value})}
                    placeholder="0"
                    className="bg-void-black border-white/10 pr-12"
                    data-testid="progress-squat-input"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">lbs</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="font-orbitron text-[10px] text-gray-500 flex items-center gap-1">
                  <Timer className="w-3 h-3" /> 40 YARD DASH
                </Label>
                <div className="relative">
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.forty_yard_dash}
                    onChange={(e) => setFormData({...formData, forty_yard_dash: e.target.value})}
                    placeholder="0.00"
                    className="bg-void-black border-white/10 pr-12"
                    data-testid="progress-40yd-input"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">sec</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="font-orbitron text-[10px] text-gray-500 flex items-center gap-1">
                  <Activity className="w-3 h-3" /> SHUTTLE RUN
                </Label>
                <div className="relative">
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.shuttle_run}
                    onChange={(e) => setFormData({...formData, shuttle_run: e.target.value})}
                    placeholder="0.00"
                    className="bg-void-black border-white/10 pr-12"
                    data-testid="progress-shuttle-input"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">sec</span>
                </div>
              </div>
            </div>
          </div>

          {/* Video Upload */}
          <div className="border border-dashed border-neon-pink/50 p-4">
            <Label className="font-orbitron text-xs text-neon-pink mb-3 flex items-center gap-2">
              <Video className="w-4 h-4" />
              PROGRESS VIDEO
            </Label>
            
            <div className="flex items-start gap-4">
              <div className="w-32 h-20 bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden flex-shrink-0">
                {formData.video_url ? (
                  <video src={formData.video_url} className="w-full h-full object-cover" />
                ) : (
                  <Video className="w-8 h-8 text-gray-600" />
                )}
              </div>

              <div className="flex-1 space-y-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="video/*"
                  onChange={(e) => handleVideoUpload(e, false)}
                  className="hidden"
                  id="progress-video-upload"
                />
                <label
                  htmlFor="progress-video-upload"
                  data-testid="progress-video-upload-btn"
                  className={`cyber-btn py-2 px-4 text-xs cursor-pointer inline-flex items-center gap-2 border-neon-pink text-neon-pink hover:bg-neon-pink hover:text-black ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      UPLOADING...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      UPLOAD VIDEO
                    </>
                  )}
                </label>
                <p className="font-rajdhani text-xs text-gray-500">Max 100MB • MP4, MOV, WebM</p>
                {formData.video_url && (
                  <p className="font-rajdhani text-xs text-neon-green">✓ Video uploaded</p>
                )}
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label className="font-orbitron text-xs text-gray-400">Notes</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              placeholder="Add any notes about this session..."
              className="bg-void-black border-white/10 min-h-[80px]"
              data-testid="progress-notes-input"
            />
          </div>

          <div className="flex justify-end">
            <button type="submit" className="cyber-btn py-2 px-6 text-xs" data-testid="save-progress-btn">
              <span className="flex items-center gap-2">
                <Save className="w-4 h-4" />
                SAVE PROGRESS
              </span>
            </button>
          </div>
        </form>
      )}

      {/* Players with Progress */}
      <div className="space-y-4">
        {players.map(player => {
          const playerProgress = progressData[player.id] || [];
          const isExpanded = expandedPlayer === player.id;
          const latestProgress = playerProgress[0];

          return (
            <div key={player.id} className="cyber-card overflow-hidden" data-testid={`player-progress-card-${player.id}`}>
              {/* Player Header */}
              <div 
                className="p-4 flex items-center justify-between cursor-pointer hover:bg-white/5 transition-colors"
                onClick={() => setExpandedPlayer(isExpanded ? null : player.id)}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/5 rounded-full overflow-hidden flex-shrink-0">
                    {player.image_url ? (
                      <img 
                        src={getImageUrl(player.image_url)} 
                        alt={player.name} 
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = DEFAULT_PLAYER_IMAGE;
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <User className="w-6 h-6 text-gray-600" />
                      </div>
                    )}
                  </div>
                  <div>
                    <h3 className="font-orbitron text-sm text-white">{player.name}</h3>
                    <p className="font-rajdhani text-xs text-gray-500">
                      {player.position} • #{player.jersey_number || '00'} • {playerProgress.length} entries
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  {latestProgress && (
                    <div className="hidden md:flex items-center gap-6 text-xs font-rajdhani">
                      {latestProgress.bench_press_max && (
                        <span className="text-neon-blue">
                          <Dumbbell className="w-3 h-3 inline mr-1" />
                          {latestProgress.bench_press_max} lbs
                        </span>
                      )}
                      {latestProgress.forty_yard_dash && (
                        <span className="text-neon-green">
                          <Timer className="w-3 h-3 inline mr-1" />
                          {latestProgress.forty_yard_dash}s
                        </span>
                      )}
                    </div>
                  )}
                  {isExpanded ? (
                    <ChevronUp className="w-5 h-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-500" />
                  )}
                </div>
              </div>

              {/* Expanded Progress Entries */}
              {isExpanded && (
                <div className="border-t border-white/10">
                  {playerProgress.length === 0 ? (
                    <div className="p-6 text-center">
                      <TrendingUp className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                      <p className="font-rajdhani text-sm text-gray-500">No progress entries yet</p>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setFormData({...formData, player_id: player.id});
                          setShowForm(true);
                        }}
                        className="mt-3 text-neon-blue text-xs font-orbitron hover:underline"
                      >
                        + Add First Entry
                      </button>
                    </div>
                  ) : (
                    <div className="divide-y divide-white/5">
                      {playerProgress.map(entry => (
                        <div key={entry.id} className={`p-4 ${editingProgress?.id === entry.id ? 'bg-neon-green/5 ring-1 ring-neon-green/30' : ''}`}>
                          {editingProgress?.id === entry.id ? (
                            /* Edit Mode */
                            <div className="space-y-4">
                              <div className="flex items-center gap-2 mb-2 pb-2 border-b border-neon-green/30">
                                <Edit className="w-4 h-4 text-neon-green" />
                                <span className="font-orbitron text-xs text-neon-green">EDITING PROGRESS</span>
                              </div>
                              
                              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                                <div>
                                  <Label className="font-orbitron text-[10px] text-gray-500">Date</Label>
                                  <Input
                                    type="date"
                                    value={editingProgress.date}
                                    onChange={(e) => setEditingProgress({...editingProgress, date: e.target.value})}
                                    className="bg-void-black border-white/10 h-8 text-xs"
                                  />
                                </div>
                                <div>
                                  <Label className="font-orbitron text-[10px] text-gray-500">Bench (lbs)</Label>
                                  <Input
                                    type="number"
                                    step="0.5"
                                    value={editingProgress.bench_press_max}
                                    onChange={(e) => setEditingProgress({...editingProgress, bench_press_max: e.target.value})}
                                    className="bg-void-black border-white/10 h-8 text-xs"
                                  />
                                </div>
                                <div>
                                  <Label className="font-orbitron text-[10px] text-gray-500">Squat (lbs)</Label>
                                  <Input
                                    type="number"
                                    step="0.5"
                                    value={editingProgress.squat_max}
                                    onChange={(e) => setEditingProgress({...editingProgress, squat_max: e.target.value})}
                                    className="bg-void-black border-white/10 h-8 text-xs"
                                  />
                                </div>
                                <div>
                                  <Label className="font-orbitron text-[10px] text-gray-500">40yd (sec)</Label>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    value={editingProgress.forty_yard_dash}
                                    onChange={(e) => setEditingProgress({...editingProgress, forty_yard_dash: e.target.value})}
                                    className="bg-void-black border-white/10 h-8 text-xs"
                                  />
                                </div>
                                <div>
                                  <Label className="font-orbitron text-[10px] text-gray-500">Shuttle (sec)</Label>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    value={editingProgress.shuttle_run}
                                    onChange={(e) => setEditingProgress({...editingProgress, shuttle_run: e.target.value})}
                                    className="bg-void-black border-white/10 h-8 text-xs"
                                  />
                                </div>
                              </div>

                              {/* Edit Video */}
                              <div className="flex items-center gap-4">
                                <div className="w-24 h-14 bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden">
                                  {editingProgress.video_url ? (
                                    <video src={editingProgress.video_url} className="w-full h-full object-cover" />
                                  ) : (
                                    <Video className="w-6 h-6 text-gray-600" />
                                  )}
                                </div>
                                <div>
                                  <input
                                    ref={editFileInputRef}
                                    type="file"
                                    accept="video/*"
                                    onChange={(e) => handleVideoUpload(e, true)}
                                    className="hidden"
                                    id={`edit-progress-video-${entry.id}`}
                                  />
                                  <label
                                    htmlFor={`edit-progress-video-${entry.id}`}
                                    className={`cyber-btn py-1 px-3 text-[10px] cursor-pointer inline-flex items-center gap-1 border-neon-pink text-neon-pink ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
                                  >
                                    {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                                    {editingProgress.video_url ? 'CHANGE' : 'UPLOAD'}
                                  </label>
                                </div>
                              </div>

                              {/* Edit Notes */}
                              <div>
                                <Label className="font-orbitron text-[10px] text-gray-500">Notes</Label>
                                <Textarea
                                  value={editingProgress.notes}
                                  onChange={(e) => setEditingProgress({...editingProgress, notes: e.target.value})}
                                  className="bg-void-black border-white/10 min-h-[60px] text-xs"
                                />
                              </div>

                              {/* Edit Actions */}
                              <div className="flex justify-between pt-2 border-t border-white/10">
                                <button
                                  onClick={() => setEditingProgress(null)}
                                  className="text-gray-500 hover:text-white transition-colors p-2 flex items-center gap-1 text-xs font-rajdhani"
                                >
                                  <X className="w-4 h-4" />
                                  Cancel
                                </button>
                                <button
                                  onClick={handleSaveEdit}
                                  className="cyber-btn py-2 px-4 text-xs border-neon-green text-neon-green hover:bg-neon-green hover:text-black"
                                >
                                  <span className="flex items-center gap-2">
                                    <Check className="w-4 h-4" />
                                    SAVE
                                  </span>
                                </button>
                              </div>
                            </div>
                          ) : (
                            /* View Mode */
                            <>
                              <div className="flex items-start justify-between mb-3">
                                <div>
                                  <span className="font-orbitron text-xs text-neon-blue">{formatDate(entry.date)}</span>
                                  {entry.notes && (
                                    <p className="font-rajdhani text-xs text-gray-500 mt-1">{entry.notes}</p>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleEdit(entry); }}
                                    className="text-gray-500 hover:text-neon-green transition-colors p-1"
                                    title="Edit"
                                    data-testid={`edit-progress-${entry.id}`}
                                  >
                                    <Edit className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleDelete(entry.id); }}
                                    className="text-gray-500 hover:text-red-500 transition-colors p-1"
                                    title="Delete"
                                    data-testid={`delete-progress-${entry.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>

                              {/* Stats Grid */}
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                                <div className="bg-neon-blue/10 border border-neon-blue/30 px-3 py-2 rounded">
                                  <div className="flex items-center gap-1">
                                    <Dumbbell className="w-3 h-3 text-neon-blue" />
                                    <span className="font-orbitron text-[10px] text-gray-400">BENCH</span>
                                  </div>
                                  <p className="font-orbitron text-sm text-neon-blue">
                                    {entry.bench_press_max || '--'} <span className="text-[10px] text-gray-500">lbs</span>
                                  </p>
                                </div>
                                <div className="bg-neon-pink/10 border border-neon-pink/30 px-3 py-2 rounded">
                                  <div className="flex items-center gap-1">
                                    <Dumbbell className="w-3 h-3 text-neon-pink" />
                                    <span className="font-orbitron text-[10px] text-gray-400">SQUAT</span>
                                  </div>
                                  <p className="font-orbitron text-sm text-neon-pink">
                                    {entry.squat_max || '--'} <span className="text-[10px] text-gray-500">lbs</span>
                                  </p>
                                </div>
                                <div className="bg-neon-green/10 border border-neon-green/30 px-3 py-2 rounded">
                                  <div className="flex items-center gap-1">
                                    <Timer className="w-3 h-3 text-neon-green" />
                                    <span className="font-orbitron text-[10px] text-gray-400">40 YD</span>
                                  </div>
                                  <p className="font-orbitron text-sm text-neon-green">
                                    {entry.forty_yard_dash || '--'} <span className="text-[10px] text-gray-500">sec</span>
                                  </p>
                                </div>
                                <div className="bg-yellow-500/10 border border-yellow-500/30 px-3 py-2 rounded">
                                  <div className="flex items-center gap-1">
                                    <Activity className="w-3 h-3 text-yellow-500" />
                                    <span className="font-orbitron text-[10px] text-gray-400">SHUTTLE</span>
                                  </div>
                                  <p className="font-orbitron text-sm text-yellow-500">
                                    {entry.shuttle_run || '--'} <span className="text-[10px] text-gray-500">sec</span>
                                  </p>
                                </div>
                              </div>

                              {/* Video */}
                              {entry.video_url && (
                                <div className="mt-3">
                                  <video 
                                    src={entry.video_url} 
                                    controls 
                                    className="w-full max-w-md h-auto rounded border border-white/10"
                                    data-testid={`progress-video-${entry.id}`}
                                  />
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {players.length === 0 && (
        <div className="text-center py-12 cyber-card" data-testid="no-players-state">
          <TrendingUp className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <p className="font-rajdhani text-gray-500">No players yet. Add players first to track their progress!</p>
        </div>
      )}
    </div>
  );
};

export default PlayerProgressManager;
